import { Controller } from '@hotwired/stimulus'
import { postRequest, getRequest, isEmpty, kebabize } from '../src/utils'

// Connects to data-controller="form"
// Used in: Website enrol form (4 slides)
// Purpose: Allow new users to sign up their student direclty to a band space or a waiting list.
// Execution: Get band space availability through school and year group, get and validate inputs related to parent and student, display values in a booking overview and conditionally ask for payment information. Finaly, allow users to sign up(parent and student)
// Explanation: This controller should be refactored into several specific controllers as many functions simply toggle elements. The school dropdown scroll and select functionality should be seperate to be shared with search controller.

// Refactor suggestions:
// 1. ShowStepTwo and StepThree Error message functions can be combined and add a parameter for which slide
// 2. Remove unused targets year and school
// 3. Reduce no. of targets by using one "input" target instead of one target per type, use the id or dataset attribute to dynamically validate.
// 4. FIX: should use trimmed value for set value not inputField
// 5. Adding to booking value should be more dynamic
// 6. 'Next' function should outsource to another function 'validate slide' and if return true go to next slide
// 7. See how to get validations value from another file to reduce overall length
// 8. The checkSlide and show error message functions should be made dynamic
// 9. Should get rid of all field specific targets and simply call them 'input', on validate it then reads their id or attribute to proccess them
// 10. HTML should be refactored for a simpler rendering, render manually and not all at once.
// 11. Adding values to booking overview should be more dynamic

export default class extends Controller {
  static niYearGroups = {
    '-1': 'Reception',
    0: 'P1',
    1: 'P2',
    2: 'P3',
    3: 'P4',
    4: 'P5',
    5: 'P6',
    6: 'P7',
    7: 'First year',
    8: 'Second year',
    9: 'Third year',
    10: 'Fourth year',
    11: 'Fifth year',
    12: 'Sixth year',
    13: 'Seventh year'
  }
  static standardYearGroups = {
    '-1': 'Pre-reception',
    0: 'Reception',
    1: 'Year 1',
    2: 'Year 2',
    3: 'Year 3',
    4: 'Year 4',
    5: 'Year 5',
    6: 'Year 6',
    7: 'Year 7',
    8: 'Year 8',
    9: 'Year 9',
    10: 'Year 10',
    11: 'Year 11',
    12: 'Year 12',
    13: 'Year 13'
  }

  static targets = [
    'slide', // the enrol form slides (total 4)

    // First slide
    'year', // unused
    'yearGroupInput',
    'school', // duplicate of schoolInput
    'schoolInput', // school input field
    'schoolDropdown', // container ul (dropdown) of schools
    'yearDropdown', // container ul (dropdown) of year group choices
    'yearDropdownField', // entire year group div with label and dropdown, unhidden once school is selected
    'btnOne', // 'check availability' button
    'stepOneMessage', // Error message div
    'yearGroupMessage', // Year group message div
    'yearGroupClarificationMessage',
    // Second slide
    'studentFirstName',
    'studentLastName',
    'medicalInfo',
    'medicalInfoText',
    'medicalInfoMessage',
    'adjustmentsInfo',
    'adjustmentsInfoText',
    'adjustmentsInfoMessage',
    'filmingConsent',
    'publicFilmingConsent',
    'vocalsSpace',
    'drumsSpace',
    'guitarSpace',
    'keyboardSpace',
    'vocalsDate',
    'drumsDate',
    'guitarDate',
    'keyboardDate',
    'slideTwoNextBtn', // 'Proceed' button
    'stepTwoMessage', // Slide 2 error message div
    'greatNewsHeading', // "Great News!" heading on slide 2

    // Third slide - parent
    'termsAndConditions', // to fix: these are two targets on slide 3
    'parentFirstName',
    'parentLastName',
    'phoneNumber',
    'email',
    'marketingConsent',

    // Third slide - payment info
    'accountHolder',
    'accountHolderFirstName',
    'accountHolderLastName',
    'accountName',
    'accountNumber',
    'sortCode',
    'accountEmail',
    // 'fillPaymentInfo', removed
    'paymentInfoFields',
    'confirmMessage',
    'accountInfo',
    'specialAccountInfo',
    'soleAccountHolder',

    // Third slide - other
    'submit', // slide 3 submit button
    'stepThreeMessage', // Slide 3 error messag div
    'serverError',
    'emailMessage',

    // Third slide - Booking overview
    'disclaimer',
    'secondaryInstrument',
    'loadingBtn',
    'secondaryInstrumentVocals',
    'secondaryInstrumentGuitar',
    'secondaryInstrumentDrums',
    'secondaryInstrumentKeyboard',
    'bookingStartDate',
    'bookingMonthlyPayment',
    'bookingFirstPaymentDate',
    'bookingFirstPaymentAmount',
    'bookingMonthlyPayment',
    'bookingFirstPaymentCovers',
    'overviewPaymentInfo' // unused
  ]

  static values = {
    index: Number, // used for showing slides
    // first slide - basic info
    school: Object,
    yearGroup: Object,
    // second slide - student info
    studentFirstName: Object,
    studentLastName: Object,
    instrumentPreference: Object,
    medicalInfo: Object,
    adjustmentsInfo: Object,
    filmingConsent: Object,
    publicFilmingConsent: Object,
    medicalInfoMessage: Object,
    adjustmentsInfoMessage: Object,
    // third slide - parent info
    secondaryInstrument: Array,
    parentFirstName: Object,
    parentLastName: Object,
    termsAndConditions: Object,
    phoneNumber: Object,
    email: Object,
    marketingConsent: Object,
    // payment info
    showPaymentInfoFields: {
      default: true,
      type: Boolean
    },
    accountHolder: Object,
    accountHolderFirstName: Object,
    accountHolderLastName: Object,
    accountName: Object,
    accountNumber: Object,
    sortCode: Object,
    accountEmail: Object,
    compulsoryPaymentInfo: {
      type: Boolean,
      default: true
    },
    // other
    irockYearGroups: Array,
    errors: Array,
    validations: Object,
    bandSpace: Object,
    priceRiseWarning: Boolean
  }

  connect() {
    // console.log('form controller connected');
    // Set input validations and event listener for the school dropdown
    this.setValidationsValue()
    this.schoolDropdownNavigator()
    let context = this
    // Set event listener that turbo stream has been rendered and call stream function
    document.addEventListener('turbo:before-stream-render', (event) =>
      this.streamed(event, context)
    )
  }

  initialize() {
    // Start enrol form on slide index 0 (given by slide value)
    this.showCurrentSlide()
  }

  setValidationsValue() {
    // Sets validations value for each input field. Used to check each input by regex and whether it was filled in
    // 'Message' is displayed at the bottom of form slide on click of 'proceed' button, 'validationMessage' is displayed underneath the specific input itself
    this.validationsValue = {
      studentFirstName: {
        message: 'Please fill in student first name',
        validationMessage:
          'Please enter a valid first name (max. length 32 characters)',
        validation: "^(?=.{1,32}$)[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$"
      },
      studentLastName: {
        message: 'Please fill in student last name',
        validationMessage:
          'Please enter a valid last name (max. length 32 characters)',
        validation: "^(?=.{1,32}$)[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$"
      },
      medicalInfo: { message: 'Please fill in medical info' },
      medicalInfoMessage: {
        message: 'Please fill in medical info',
        validationMessage:
          'Please enter medical info (max. length 1000 characters)',
        validation: '^.{1,1000}$'
      },
      adjustmentsInfo: { message: 'Please fill in adjustments info' },
      adjustmentsInfoMessage: {
        message: 'Please fill in adjustments info',
        validationMessage:
          'Please enter adjustments info (max. length 1000 characters)',
        validation: '^.{1,1000}$'
      },
      instrumentPreference: { message: 'Please select an instrument' },
      filmingConsent: {
        message: 'Please select filming consent'
      },
      publicFilmingConsent: {
        message: 'Please select marketing consent'
      },
      parentFirstName: {
        message: 'Please fill in parent first name',
        validationMessage:
          'Please enter a valid name (max. length 32 characters)',
        validation: "^(?=.{1,32}$)[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$"
      },
      parentLastName: {
        message: 'Please fill in parent last name',
        validationMessage:
          'Please enter a valid name (max. length 32 characters)',
        validation: "^(?=.{1,32}$)[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$"
      },
      phoneNumber: {
        message: 'Please fill in phone number',
        validationMessage: 'Please enter a valid phone number',
        validation: '^\\+?[\\d\\s-]{3,15}$'
      },
      email: {
        message: 'Please fill in your email',
        validationMessage: 'Please enter a valid email (max. 50 characters)',
        validation: '^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9.-]{4,50}$'
      },
      marketingConsent: { message: 'Please select marketing consent' },
      accountHolderValue: {
        message:
          'Please fill check that you are the account holder and sole signatory'
      },
      accountHolderFirstName: {
        message: 'Please fill in account holder first name',
        validationMessage:
          'Please enter a valid first name (max. 32 characters)',
        validation: `^(?=.{1,32}$)[a-zA-Z]+(([',\. \-][a-zA-Z ])?[a-zA-Z]*)*$`
      },
      accountHolderLastName: {
        message: 'Please fill in account holder last name',
        validationMessage:
          'Please enter a valid last name (max. 32 characters)',
        validation: "^(?=.{1,32}$)[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$"
      },
      accountName: {
        message: 'Please fill in account name',
        validationMessage:
          'Please enter a valid account name with 3-18 characters',
        validation: '^.{3,18}$'
      },
      accountNumber: {
        message: 'Please fill in account number',
        validationMessage: 'Please enter a valid 8 digit account number',
        validation: '^\\d{8}$'
      },
      sortCode: {
        message: 'Please fill in account sort code',
        validationMessage: 'Please enter a valid 6 digit sort code',
        validation: '^\\d{6}$'
      },
      accountEmail: {
        message: 'Please fill in your account email',
        validationMessage:
          'Please enter a valid email (max. length 50 characters)',
        validation: '^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9.-]{1,50}$'
      },
      termsAndConditions: {
        message: 'Please accept terms and conditions'
      },
      accountHolder: {
        message:
          'Please check whether you are the account holder and sole signatory'
      }
    }
  }

  validate(event) {
    // On change, validate inputs from slide 2 and 3 (including in text, radio and instrument website form partials). If the inputs pass validation they are ascribed to a value. If all neccessary values are present, the user can proceed to the next slide. Inputs that do not pass validation are not set and thus user can not proceed

    // Set the input field by currentTarget dataset.inputField, these are set in slide 2,3 and partials as data-input-field
    let { inputField } = event.currentTarget.dataset
    // Validate text input and set values if passed, set values directly for radio inputs
    let inputType = event.currentTarget.type
    // If input is checkbox
    if (inputType === 'checkbox') {
      this.setValues(event, inputField)
    } else if (inputType !== 'radio') {
      // Trim input value and find and test the corresponding validation regex in validations value
      // FIX: should use trimmed value for set value not inputField
      let input = this[`${inputField}Target`].value.trim()
      let validation = this.validationsValue[`${inputField}`].validation
      let regex = new RegExp(`${validation}`)
      let validInput = regex.test(input)
      if (validInput) {
        // If test passed, hide the corresponding error message and set values
        this.hideInputErrorMessage(event, inputField)
        this.setValues(event, inputField)
      } else {
        // If test failed, reset the value and show error message
        this.resetValue(inputField)
        this.showInputErrorMessage(event, inputField)
      }
    } else {
      // Set values directly for radio inputs as they do not need to be checked against a regex
      this.setValues(event, inputField)
    }
    // If errors exist, check if the slide is ok and show the corresponding slide message
    // FIX: It should be unnessary to call 'slide ok' functions here as they return true/false
    if (this.errorsValue.length > 0) {
      if (this.indexValue == 1) {
        this.slideTwoOk()
        this.showStepTwoMessage()
      }
      if (this.indexValue == 2) {
        this.slideThreeOk()
        this.showStepThreeMessage()
      }
    }
  }

  showInputErrorMessage(event, inputField) {
    // Show the corresponding validation message to a users failed input
    let message = this.validationsValue[inputField].validationMessage
    let errorDiv = document.getElementById(`${inputField}-error-message`)
    errorDiv.innerText = message
    errorDiv.classList.remove('hidden')
  }

  hideInputErrorMessage(event, inputField) {
    // Hide the corresponding validation message to a users input
    let errorDiv = document.getElementById(`${inputField}-error-message`)
    errorDiv.classList.add('hidden')
  }

  resetValue(inputField) {
    // Set a value that corresponds to an 'input field' to empty object
    this[`${inputField}Value`] = {}
  }

  setValues(event, inputField) {
    // Set values that correspond to the 'input field' and show it on booking overview
    let context = this
    // If input is type radio

    if (event.currentTarget.type == 'radio') {
      // If radio input is for an instrument
      if (event.currentTarget.name == 'instrument') {
        // Set instrument preference for student
        const instrument = event.currentTarget.value

        // unselect any secondary instruments choices if they are selected
        let targets = [
          this.secondaryInstrumentVocalsTarget,
          this.secondaryInstrumentKeyboardTarget,
          this.secondaryInstrumentDrumsTarget,
          this.secondaryInstrumentGuitarTarget
        ]
        targets.forEach((target) => {
          if (target.checked) {
            target.removeAttribute('disabled')
            target.click()
          }
        })
        // select the same instrument preference for secondary choices
        if (instrument == 'guitar') {
          this.secondaryInstrumentGuitarTarget.click()
          this.secondaryInstrumentGuitarTarget.setAttribute('disabled', true)
        } else if (instrument == 'drums') {
          this.secondaryInstrumentDrumsTarget.click()
          this.secondaryInstrumentDrumsTarget.setAttribute('disabled', true)
        } else if (instrument == 'keyboard') {
          this.secondaryInstrumentKeyboardTarget.click()
          this.secondaryInstrumentKeyboardTarget.setAttribute('disabled', true)
        } else if (instrument == 'vocals') {
          this.secondaryInstrumentVocalsTarget.click()
          this.secondaryInstrumentVocalsTarget.setAttribute('disabled', true)
        } else {
        }
        this.instrumentPreferenceValue = { value: event.currentTarget.value }
        // If the instrument has a band space, set band space value, show payment fields on slide 3, check 'wish to provide payment info' and change 'proceed' button text
        let { bandSpace } = event.currentTarget.dataset
        if (bandSpace) {
          this.bandSpaceValue = JSON.parse(bandSpace)
          this.showPaymentInfoFields()
          // if (this.hasPaymentInfoFieldsTarget) {
          //   document.getElementById('fill-payment-info-yes').checked = true;
          // }
          this.slideTwoNextBtnTarget.innerText = 'Save my spot'
          this.submitTarget.innerText = 'Book now'
        } else {
          // If instrument has no band space, clear band space value and change 'proceed' button text
          this.bandSpaceValue = {}
          if (this.schoolValue.status == 'created') {
            this.slideTwoNextBtnTarget.innerText = 'Confirm'
            this.submitTarget.innerText = 'Register interest'
          } else {
            this.slideTwoNextBtnTarget.innerText = 'Join waiting list'
            this.submitTarget.innerText = 'Book now (join waiting list)'
          }
        }

        // Add insrument preference to corresponding 'booking overview' field
        if (document.getElementById(`overview-${kebabize(inputField)}`)) {
          document.getElementById(
            `overview-${kebabize(inputField)}`
          ).innerText = event.currentTarget.value
        }
      } else {
        // If regular radio (not instrument choice)
        // If radio input is 'terms and conditions' and termsAndconditionsTarget is not checked clear terms and conditions value
        if (
          inputField == 'termsAndConditions' &&
          !this[`${inputField}Target`].checked
        ) {
          this[`${inputField}Value`] = {}
        } else {
          // If any other radio input or termsAndConditionsTarget is checked add value
          this[`${inputField}Value`] = {
            value: this[`${inputField}Target`].checked
          }
        }

        // Add the radio value to booking overview
        let text = this[`${inputField}Value`].value == true ? 'Yes' : 'No'
        if (document.getElementById(`overview-${kebabize(inputField)}`)) {
          document.getElementById(
            `overview-${kebabize(inputField)}`
          ).innerText = text
        }
      }
    } else if (event.currentTarget.type == 'checkbox') {
      if (event.currentTarget.checked) {
        let arr = context.secondaryInstrumentValue
        arr.push(event.currentTarget.value)
        context.secondaryInstrumentValue = arr
      } else {
        context.secondaryInstrumentValue =
          context.secondaryInstrumentValue.filter(
            (thing) => thing !== event.currentTarget.value
          )
      }
    } else {
      // If text input, directly set value from 'input target.value'
      this[`${inputField}Value`] = { value: this[`${inputField}Target`].value }
      // Add text input to booking overview
      if (document.getElementById(`overview-${kebabize(inputField)}`)) {
        document.getElementById(`overview-${kebabize(inputField)}`).innerText =
          this[`${inputField}Target`].value
      }
    }
  }

  async next() {
    // return this.indexValue++;
    // On click of 'proceed' button, check slide index value and hide or show corresponding error messages if errors exist. If no errors, proceed to the next slide.
    if (this.indexValue == 0) {
      if (this.slideOneOk()) {
        this.hideStepOneMessage()
        // Scroll to the slide 2 container
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
        window.scrollTo(0, 0)
        requestAnimationFrame(() => {
          this.slideTargets[1].scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
        this.indexValue++
        // Add band space information corresponding to year group and school
        this.checkAvailability()
        let excludingMonths =
          this.schoolValue.country == 'northern_ireland'
            ? 'July and August'
            : 'August'
        // If a school has timetable, add weekdays to the welcome message on slide 2
        let dayOfWeek = this.schoolValue.weekdays
          ? this.schoolValue.weekdays
          : 'a given day (to be confirmed)'
        // Welcome message on slide 2
        if (this.schoolValue.status == 'created') {
          this.confirmMessageTarget.innerHTML = `<span>We are currently registering sign ups for iRock lessons at <span class="font-semibold">${this.schoolValue.name}</span>. Band lessons are <span class="font-semibold">30 minutes per week on ${dayOfWeek}</span>  for a monthly subscription of <span class="font-semibold">£${this.schoolValue.pounds}</span>. To register your interest, please fill in the information below.</span>`
        } else {
          this.confirmMessageTarget.innerHTML = `<span>iRock lessons take place at <span class="font-semibold">${this.schoolValue.name}</span>. Band lessons are <span class="font-semibold">30 minutes per week on ${dayOfWeek}</span>  for a monthly subscription of <span class="font-semibold">£${this.schoolValue.pounds} (excl. ${excludingMonths})</span>. To join, please fill in the information below.</span>`
        }
      } else {
        // If errors exist, show errors
        this.showStepOneMessage()
      }
    } else if (this.indexValue == 1) {
      // On slide 2, if no errors allow user to proceed to slide 3
      if (this.slideTwoOk()) {
        // Scroll to top BEFORE changing slide for mobile Safari
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
        window.scrollTo(0, 0)
        requestAnimationFrame(() => {
          this.slideTargets[0].scrollIntoView({ behavior: 'auto', block: 'start' })
        })
        this.indexValue++
        this.hideStepTwoMessage()
      } else {
        // If errors exist, show slide 2 error messages
        this.showStepTwoMessage()
      }
    } else if (this.indexValue == 2) {
      // Check if slide 3 is ok, then book the spot with post request
      if (this.slideThreeOk()) {
        this.submitTarget.classList.add('hidden')
        this.loadingBtnTarget.classList.remove('hidden')
        try {
          let result = await this.book()
          // If the booking was succesful, go to slide 4
          if (result.result == 'success') {
            // this.indexValue++;
            window.location.href = result.redirect_uri
            // setTimeout(window.scrollTo(0, 0), 200);
            // setTimeout(() => {
            //   window.scrollBy(0, -50); // Scrolls down by 50px
            // }, 100);
          } else {
            // If booking was not successful
            console.log(`Error occurred after trying to book space`)
            console.log(`Error:`, result)
            this.loadingBtnTarget.classList.add('hidden')
            this.submitTarget.classList.remove('hidden')
            // If the email is already in the system, ask to
            if (result.error == 'Email is taken') {
              this.emailMessageTarget.classList.remove('hidden')
            } else {
              // If there was a different type of (unknown) error
              this.serverErrorTarget.classList.remove('hidden')
            }
          }
        } catch (error) {
          console.log(error)
        }
        // If booking was not successful, then show error message
      } else {
        this.showStepThreeMessage()
      }
    }
  }

  slideOneOk() {
    // Check if user succesfully selected school and year group
    return !this.noValue(this.schoolValue) && !this.noValue(this.yearGroupValue)
  }

  slideTwoOk() {
    // Check if no errors on slide 2 inputs. Inputs who's validation is not succesful are not added to values
    let errors = ['instrumentPreferenceValue']
    // Fields that everyone needs to fill out
    if (this.hasStudentFirstNameTarget) {
      errors.push(
        'studentFirstNameValue',
        'studentLastNameValue',
        'filmingConsentValue',
        'publicFilmingConsentValue',
        'medicalInfoValue',
        'adjustmentsInfoValue'
      )
    }

    errors = errors.filter((value) => {
      return this.noValue(this[value])
    })

    this.errorsValue = errors
    return errors.length == 0
  }

  slideThreeOk() {
    // Returns true if no errors (missing values) on slide 3
    let errors = ['termsAndConditionsValue']
    // Fields that everyone needs to fill out
    if (this.hasParentFirstNameTarget) {
      errors.push(
        'parentFirstNameValue',
        'parentLastNameValue',
        'phoneNumberValue',
        'emailValue',
        'marketingConsentValue'
      )
    }

    // Add account holder value to errors if user wants or needs to input payment info
    if (this.showPaymentInfoFieldsValue && this.hasPaymentInfoFieldsTarget) {
      errors.push('accountHolderValue')
    }

    const paymentErrors = [
      'accountHolderFirstNameValue',
      'accountHolderLastNameValue',
      'accountNameValue',
      'accountNumberValue',
      'sortCodeValue',
      'accountEmailValue'
    ]

    // If user is account holder and payment info fields are shown, add payment errors to slide 3 errors
    if (this.hasPaymentInfoFieldsTarget) {
      if (this.accountHolderTarget.checked && this.showPaymentInfoFieldsValue) {
        paymentErrors.forEach((err) => {
          errors.push(err)
        })
      }
    }

    // Return true if no errors
    errors = errors.filter((value) => {
      return this.noValue(this[value])
    })

    this.errorsValue = errors
    return errors.length == 0
  }

  showStepOneMessage() {
    this.stepOneMessageTarget.classList.remove('hidden')
  }

  showStepTwoMessage() {
    // Show error message field and populate it with the corresponding error messages from validations value
    this.stepTwoMessageTarget.classList.remove('hidden')
    let errors = this.errorsValue.map((error) => {
      return this.validationsValue[error.replace('Value', '')].message
    })
    let errorMessages = ''
    errors.forEach((error) => {
      errorMessages += `${error}\n`
    })
    this.stepTwoMessageTarget.innerText = errorMessages
  }

  showStepThreeMessage() {
    // Show error message field and populate it with the corresponding error messages from validations value
    this.stepThreeMessageTarget.classList.remove('hidden')
    let errors = this.errorsValue.map((error) => {
      return this.validationsValue[error.replace('Value', '')].message
    })
    let errorMessages = ''
    errors.forEach((error) => {
      errorMessages += `${error}\n`
    })
    this.stepThreeMessageTarget.innerText = errorMessages
  }

  hideStepOneMessage() {
    this.stepOneMessageTarget.classList.add('hidden')
  }

  hideStepTwoMessage() {
    this.stepTwoMessageTarget.classList.add('hidden')
  }

  processInputs() {
    // If on slide 1, process step one
    if (this.indexValue == 0) {
      this.processStepOne()
    }
  }

  processStepOne() {
    // FIX: the condition is not correct as school value and yeargroup value always 'exist', therefore the button is always 'enabled'. The html button doesn't have disabled attribute in the first place and this attribute is also not re-added when school value is removed. In fact, school value is likely never re-assigned once a school has been selected
    // If school value and year value are present, enable 'proceed' button
    if (this.schoolValue && this.yearGroupValue) {
      this.btnOneTarget.removeAttribute('disabled')
    } else {
      // Show the mesage saying please select a school and a year group
    }
  }

  async checkAvailability() {
    // Get spaces availability based on school and year group and then rended the band spaces turbo stream
    try {
      let resp = await this.fetchSpacesAvailability()
      let html = await resp.text()
      await Turbo.renderStreamMessage(html)
    } catch (error) { }
  }

  async fetchSpacesAvailability() {
    // Use school and band space to get band space information for each instrument
    try {
      return await getRequest(
        `/api/v1/band_spaces/get_availability?school_id=${this.schoolValue.id}&year_group=${this.yearGroupValue.value}`
      )
    } catch (error) {
      throw error
    }
  }

  previous() {
    // Go back one slide
    if (this.indexValue == 1) {
      location.reload()
    } else {
      this.indexValue--
    }
  }

  indexValueChanged() {
    // When index value has changed, show current slide and hide the others
    this.showCurrentSlide()
  }

  showCurrentSlide() {
    // Hide elements whose index value in slidetarget array does not correspond to the current index value
    this.slideTargets.forEach((element, index) => {
      element.hidden = index !== this.indexValue
    })
  }

  selectYear() {
    // Toggle the school year dropdown
    this.yearDropdownTarget.classList.toggle('hidden')
  }

  confirmYearGroup(event) {
    // On click on year group li, get the year group value and name from currentTarget dataset, toggle the dropdown, insert the year group name into the (readonly) input and store the 'year group value'
    let { yearGroupValue, yearGroupName } = event.currentTarget.dataset
    this.yearDropdownTarget.classList.toggle('hidden')
    this.yearGroupInputTarget.value = yearGroupName
    this.yearGroupValue = {
      name: yearGroupName,
      value: yearGroupValue
    }
    // Set the booking overview field
    document.getElementById(`overview-year-group`).innerText = yearGroupName
  }

  async selectSchool(event) {
    // On input of school input field, once 3 letters, fetch schools with similar names and populate the dropdown
    let school = this.schoolTarget.value.trim()
    if (school.length > 2) {
      try {
        let schools = await this.fetchSchools(school)
        this.populateSchoolDropdown(schools)
        this.allSchoolsValue = schools
      } catch (error) {
        console.log(error)
      }
      // 'Active school dropdown' is the currently highlighted school
      // If active school dropdown element exists, remove it from all schools and add it to the first first child of the school dropdown
      const activeSchoolDropdowns = document.querySelectorAll(
        '.active-school-dropdown'
      )

      if (activeSchoolDropdowns.length > 0) {
        activeSchoolDropdowns.forEach((li) => {
          li.classList.remove('active-school-dropdown')
        })
      }
      this.schoolDropdownTarget.firstChild?.classList?.add(
        'active-school-dropdown'
      )
    } else {
      // If the input length is not more than 2 charachters, remove schools from dropdown and hide it, set all schools value to an empty array
      let schools = []
      this.populateSchoolDropdown(schools)
      this.allSchoolsValue = schools
      this.schoolDropdownTarget.classList.add('hidden')
    }
  }

  populateSchoolDropdown(schools) {
    // Get the attributes for each school and populate the ul with an li for each school with the attributes as data. These data points are used on 'confirmSchool' when user selects a school
    let html = ''
    if (!schools || schools.length === 0) {
      html = `<li class="w-full p-2">
        <div class="border border-gray-300 px-4 py-3 flex rounded-xl bg-gray-100 flex w-full">
          <div class="text-brand-dark-blue">
          Don't see your school?
          </div>
          <a href="https://form.jotform.com/243032843233044" target="_blank" id="school-mail" class="hover:underline text-brand-primary ml-1" id="option-0">Register interest</a>
        </div>
      </li>`
      this.schoolDropdownTarget.innerHTML = html

      if (html) {
        this.schoolDropdownTarget.classList.remove('hidden')
      } else {
        this.schoolDropdownTarget.classList.add('hidden')
      }
      return
    }

    schools.forEach((school) => {
      let {
        name,
        postalCode,
        temporary,
        status,
        irockYearGroups,
        irockDays,
        tier,
        id,
        country
      } = school.attributes
      // If no postal code, use empty string, otherwise this might show an error for schools without postal code
      postalCode = postalCode ? postalCode : ''
      status = status ? status : ''
      html += `<li data-action="click->form#confirmSchool" data-school-id="${id}" data-school-country="${country}" data-school-price="${tier?.price_cents}" data-school-temporary="${temporary}" data-school-status="${status}" data-school-weekdays="${irockDays}" data-school-name="${name}" data-irock-year-groups="${irockYearGroups}" class="relative cursor-default select-none m-2 rounded-xl py-2 pl-3 pr-9 text-brand-dark-blue hover:bg-brand-primary hover:text-white group flex flex-col justify-center" id="option-0" role="option" tabindex="-1">
            <span class="inline-block">${name}</span>
            <span class="inline-block whitespace-pre text-xs">${postalCode} </span>
          </li>`
    })
    // If no schools match the user input, show a default button
    html += `
      <li class="w-full p-2">
        <div class="border border-gray-300 px-4 py-3 flex rounded-xl bg-gray-100 flex w-full">
          <div class="text-brand-dark-blue">
          Don't see your school?
          </div>
          <a href="https://form.jotform.com/243032843233044" target="_blank" id="school-mail" class="hover:underline text-brand-primary ml-1" id="option-0">Register interest</a>
        </div>
      </li>
      `
    this.schoolDropdownTarget.innerHTML = html
    // If the user has entered less than 3 charachters, 'schools' length is undefined and thus html variable also undefined. Depending on this the school dropdown is toggled
    if (html) {
      this.schoolDropdownTarget.classList.remove('hidden')
    } else {
      this.schoolDropdownTarget.classList.add('hidden')
    }
  }

  async fetchSchools(value) {
    // Get list of schools that are 'like' the user input (limited to 5 normal and 5 temporary schools from backend).
    // FIX: This needs to be fixed on backend to disregard non alphabet charachters.
    try {
      let response = await getRequest(
        `/api/v1/schools?name_without_special_chars_cont=${value}`
      )
      let data = await response.json()
      return data.data
    } catch (error) {
      throw error
    }
  }

  displayYearGroup(yearGroup) {
    if (this.schoolValue.country === 'northern_ireland') {
      return this.constructor.niYearGroups[yearGroup] || 'Unknown Year Group'
    } else {
      return (
        this.constructor.standardYearGroups[yearGroup] || 'Unknown Year Group'
      )
    }
  }

  confirmSchool(event) {
    // When user confirms the school, take it's dataset and set the values (unless it's a temporary school).
    let school = event.currentTarget
      ? event.currentTarget.dataset
      : event.dataset
    let { schoolTemporary, schoolName } = school

    // Redirect temporary schools to the original register interest form and hide the school dropdown
    if (schoolTemporary == 'true') {
      this.schoolInputTarget.value = schoolName
      this.schoolDropdownTarget.classList.add('hidden')
      // used for integrating 123 form directly in the enrol form
      // this.indexValue = 4;
      // this.fillRegisterInterestForm(schoolName);
      let url = '/students_form'
      return (window.location.href = url)
    }
    // Assign specific school values from school variable selected by user
    let {
      schoolId,
      irockYearGroups,
      schoolStatus,
      schoolPrice,
      schoolWeekdays,
      schoolCountry
    } = school
    // Convert school price to pounds
    let pounds = Number(schoolPrice) / 100
    // Hide school dropdown
    this.schoolDropdownTarget.classList.add('hidden')
    if (schoolName) {
      // Add school name to input box
      var years = irockYearGroups.split(',')
      years.unshift((parseInt(years[0]) - 1).toString())
      this.irockYearGroupsValue = years
      this.schoolInputTarget.value = schoolName
      this.schoolValue = {
        id: schoolId,
        name: schoolName,
        irockYearGroups,
        pounds,
        status: schoolStatus,
        weekdays: schoolWeekdays,
        country: schoolCountry
      }
      if (this.schoolValue.status == 'created') {
        this.btnOneTarget.innerText = 'Register interest'
        this.slideTwoNextBtnTarget.innerText = 'Confirm'
        this.submitTarget.innerText = 'Register interest'
      } else {
        this.btnOneTarget.innerText = 'Check availability'
      }
      // Show the yeargroup dropdown
      this.yearDropdownFieldTarget.classList.remove('hidden')
      const availableYearGroups =
        schoolCountry === 'northern_ireland'
          ? this.constructor.niYearGroups
          : this.constructor.standardYearGroups

      const yearGroups = Object.entries(availableYearGroups).filter(([k, v]) => this.irockYearGroupsValue.includes(k)).reduce((groups, [year, name]) => ({ ...groups, [year]: name }), {})

      const sortedKeys = Object.keys(yearGroups).sort((a, b) => a - b)

      const yearGroupItems = sortedKeys
        .map((value) => {
          const name = yearGroups[value]
          return `
          <li data-year-group-value="${value}" data-year-group-name="${name}" data-action="click->form#confirmYearGroup" class="relative cursor-default select-none m-2 rounded-xl py-2 pl-3 pr-9 text-gray-900 hover:bg-brand-primary hover:text-white" id="listbox-option-0" role="option">
                  <div class="font-normal block truncate">${name}</div>
                </li>`
        })
        .join('')

      this.yearDropdownTarget.innerHTML = yearGroupItems
    } else {
      // If the user selects the 'no school was found li', redirect to contacts page
      document.getElementById('school-mail').click()
    }
    // Assign the school name to booking overview (shouldn't this only happen if there is a school name?)
    document.getElementById(`overview-school-name`).innerText = schoolName
  }

  hideMedicalInfo() {
    // Hide medical info text box and restore the medical info message value, hide the corresponding booking overview field and restore it's inner text
    this.medicalInfoTextTarget.classList.add('hidden')
    this.medicalInfoMessageValue = {}
    document.getElementById('medical-info-details').classList.add('hidden')
    document.getElementById(`overview-medical-info-message`).innerText = ''
  }

  showMedicalInfo() {
    // Show to medical info text field and add it's value
    this.medicalInfoTextTarget.classList.remove('hidden')
    // FIX: this value is added early and in this step is added as empty string
    this.medicalInfoMessageValue = {
      value: this.medicalInfoMessageTarget.value
    }
    // Show medical info div booking overview and add text to child div
    document.getElementById('medical-info-details').classList.remove('hidden')
    document.getElementById(`overview-medical-info-message`).innerText =
      this.medicalInfoMessageValue.value
  }

  hideAdjustmentsInfo() {
    this.adjustmentsInfoTextTarget.classList.add('hidden')
    this.adjustmentsInfoMessageValue = {}
    document.getElementById('adjustments-info-details').classList.add('hidden')
    document.getElementById(`overview-adjustments-info-message`).innerText = ''
  }

  showAdjustmentsInfo() {
    // Show to medical info text field and add it's value
    this.adjustmentsInfoTextTarget.classList.remove('hidden')
    // FIX: this value is added early and in this step is added as empty string
    this.adjustmentsInfoMessageValue = {
      value: this.adjustmentsInfoMessageTarget.value
    }
    // Show medical info div booking overview and add text to child div
    document.getElementById('adjustments-info-details').classList.remove('hidden')
    document.getElementById(`overview-adjustments-info-message`).innerText =
      this.adjustmentsInfoMessageValue.value
  }

  checkYearGroupCompatability() {
    let yearGroupValue = parseInt(this.yearGroupValue.value)
    let yearGroup = this.displayYearGroup(yearGroupValue)
    let futureYearGroup = this.displayYearGroup(yearGroupValue + 1)
    // Get the current date
    const currentDate = new Date()

    // Extract the month and day from the current date
    const month = currentDate.getMonth() // Months are zero-based (0 = January, 6 = July)
    const day = currentDate.getDate()

    const year = currentDate.getFullYear()

    // Check if today is between 1st May and 31st July
    if (month >= 4 && month <= 6) {
      this.yearGroupClarificationMessageTarget.innerHTML = `<div class="py-2 my-3 rounded-xl bg-red-100 text-red-900
        ">Please make sure that they are <span class="font-medium" >currently in ${yearGroup}</span> and will be in <span class="font-medium" > ${futureYearGroup} in September ${year}</span></div>`
    }
    // Check if today is between 1st August and 31st August
    else if (month === 7 && day >= 1 && day <= 31) {
      this.yearGroupClarificationMessageTarget.innerHTML = `<div class="py-2 my-3 rounded-xl bg-red-100 text-red-900
        ">Please make sure that they will be in <span class="font-medium" > ${yearGroup}</span> September ${year}</span></div>`
    }
    // For any other dates
    else {
    }
    // If the school irock year groups don't include the year group input value show a message that school is still being set up or what the grades are that band lessons do take place at said school and hide 'proceed' button.
    // console.log('this.yearGroupValue', this.yearGroupValue);
    if (!this.schoolValue.irockYearGroups.includes(this.yearGroupValue.value)) {
      // Check if the year group is lower, if yes display a message to join waiting list
      const yearGroupsArray = this.schoolValue.irockYearGroups
        .split(',')
        .map(Number)
      const greatestNumber = Math.max(...yearGroupsArray)
      const yearGroupInt = parseInt(this.yearGroupValue.value)
      const isLower = yearGroupInt < greatestNumber

      if (!this.schoolValue.irockYearGroups) {
        this.yearGroupMessageTarget.innerHTML = `<span class="py-2 my-3 text-brand-primary text-sm text-semibold text-center">We are still setting up lessons for this school, please come back later</span>`
        this.btnOneTarget.classList.add('hidden')
      } else {
        const yearGroupsArray = this.schoolValue.irockYearGroups
          .split(',')
          .map(Number)
        const transformedYearGroups = yearGroupsArray
          .map((yearGroup) => this.displayYearGroup(yearGroup))
          .join(', ')

        if (isLower) {
          this.btnOneTarget.classList.remove('hidden')
          this.btnOneTarget.innerText = 'Join Waiting List'
          this.yearGroupMessageTarget.innerHTML = `<span class="py-2 my-3 text-brand-primary text-sm text-semibold text-center">We only provide band lessons in this school for years ${transformedYearGroups} but we would love for you to join the waiting list.</span>`
        } else {
          this.btnOneTarget.classList.add('hidden')
          this.yearGroupMessageTarget.innerHTML = `<span class="py-2 my-3 text-brand-primary text-sm text-semibold text-center">Sorry, we only provide band lessons in this school for years ${transformedYearGroups}</span>`
        }
      }
    } else {
      // If the input year group does match a school irock year group, show the 'proceed' button and restore the error message
      this.btnOneTarget.classList.remove('hidden')
      this.btnOneTarget.innerText = 'Check Availability'
      this.yearGroupMessageTarget.innerHTML = ''
    }
  }

  yearGroupValueChanged(value) {
    // Check year group compatibility on change of year group value and school value exits and enable 'proceed' button
    // FIX: process inputs 'enables' proceed button but this button is never actually disabled
    if (!this.noValue(this.schoolValue)) {
      this.checkYearGroupCompatability()
    }
    this.processInputs()
  }

  schoolValueChanged(value) {
    // Check year group compatibility if school value has changed and year group is provided
    // FIX: process inputs 'enables' proceed button but this button is never actually disabled
    if (!this.noValue(this.yearGroupValue)) {
      this.checkYearGroupCompatability()
    }
    this.processInputs()
  }

  async book() {
    // Create the user, their student, and payment info (if applicable) in the system with the values provided
    let user = {
      email: this.emailValue.value,
      first_name: this.parentFirstNameValue.value,
      last_name: this.parentLastNameValue.value,
      mobile: this.phoneNumberValue.value,
      marketing_consent: this.marketingConsentValue.value
    }

    let student = {
      first_name: this.studentFirstNameValue.value,
      last_name: this.studentLastNameValue.value,
      year_group: this.yearGroupValue.value,
      school_id: this.schoolValue.id,
      preferred_instrument: this.instrumentPreferenceValue.value,
      filming_consent: this.filmingConsentValue.value,
      public_filming_consent: this.publicFilmingConsentValue.value,
      secondary_instruments: this.secondaryInstrumentValue
    }

    // If the parent provided medical info message, add medical info message
    if (!this.noValue(this.medicalInfoMessageValue))
      student.medical_info = this.medicalInfoMessageValue.value
    if (!this.noValue(this.adjustmentsInfoMessageValue))
      student.adjustments_info = this.adjustmentsInfoMessageValue.value
    let body = { user, student }

    // If payment info filled, add payment info as a single value (object)
    if (this.paymentInfoFilled()) {
      let subscription = {
        account_number: this.accountNumberValue.value,
        account_name: this.accountNameValue.value,
        account_email: this.accountEmailValue.value,
        sort_code: this.sortCodeValue.value,
        first_name: this.accountHolderFirstNameValue.value,
        last_name: this.accountHolderLastNameValue.value
      }
      body.subscription = subscription
    }
    try {
      let resp = await postRequest('/parent/users', body)
      return await resp.json()
    } catch (error) {
      throw error
    }
  }

  noValue(object) {
    // Return true if value is an empty object (needed because {} != undefined) or if every key of the object has falsy values
    if (isEmpty(object)) return true

    return Object.keys(object).every(
      (key) => !object[key] && ![false, 0].includes(object[key])
    )
  }

  schoolDropdownNavigator() {
    // Add an event listener to window for users to scroll and select a school from the schools dropdown
    // On keydown, the 'active school li' is set to the first 'active-school-dropdown' element
    let context = this
    window.addEventListener('keydown', (e) => {
      // FIX: this should only be called when schools input is in focus, not every time
      let activeSchoolLi = document.querySelectorAll(
        '.active-school-dropdown'
      )[0]
      // If 'active school li' exists, set 'li index' as array of all school li's
      if (activeSchoolLi) {
        let liIndex = Array.from(activeSchoolLi.parentElement.children)
        // Find the index of the current active school li
        let currentLiIndex = liIndex.indexOf(activeSchoolLi)
        if (e.which === 13) {
          // On 'enter' key, confirm the actively selected school
          context.confirmSchool(activeSchoolLi)
        } else if (e.which === 38 && currentLiIndex != 0) {
          // On up key, unless active school li is the first school in the list, move the active-school-dropdown class one up and remove it from the previous one
          activeSchoolLi.previousElementSibling.classList.add(
            'active-school-dropdown'
          )
          document
            .querySelectorAll('.active-school-dropdown')[1]
            .classList.remove('active-school-dropdown')

          // Scroll up
          let height = activeSchoolLi.offsetHeight
          let currentLiHeight = height * currentLiIndex - 52
          context.schoolDropdownTarget.scroll({
            top: currentLiHeight,
            behavior: 'smooth'
          })
        } else if (e.which === 40 && currentLiIndex != liIndex.length - 1) {
          // On down key, if not last element, move active class down and scroll down
          activeSchoolLi.nextElementSibling.classList.add(
            'active-school-dropdown'
          )
          activeSchoolLi.classList.remove('active-school-dropdown')
          // Scroll down
          let height = activeSchoolLi.offsetHeight + 6
          let currentLiHeight = height * currentLiIndex
          context.schoolDropdownTarget.scroll({
            top: currentLiHeight,
            behavior: 'smooth'
          })
        }
      }
    })
  }

  fillAccountHolder() {
    // If user states they are the account holder, inser their name into the payment fields and show the account info fields
    this.accountHolderValue = { value: true }

    // if (this.parentAccountValue !== true) {

    //   if (this.parentFirstNameValue.value) this.accountHolderFirstNameTarget.value = this.parentFirstNameValue.value;
    //   this.fireOnChange(this.accountHolderFirstNameTarget);

    //   if (this.parentLastNameValue.value) this.accountHolderLastNameTarget.value = this.parentLastNameValue.value;
    //   this.fireOnChange(this.accountHolderLastNameTarget);

    //   if (this.emailValue.value) this.accountEmailTarget.value = this.emailValue.value;
    //   this.fireOnChange(this.accountEmailTarget);
    // }

    this.showAccountInfo()
  }

  clearAccountHolder() {
    // Clear the account holder input fields when "not sole account" so that values are not transmittet as part of payment info and can be re-added if select 'sole account holder'
    this.accountHolderValue = { value: false }

    this.accountHolderFirstNameTarget.value = ''
    this.fireOnChange(this.accountHolderFirstNameTarget)

    this.accountHolderLastNameTarget.value = ''
    this.fireOnChange(this.accountHolderLastNameTarget)

    this.accountEmailTarget.value = ''
    this.fireOnChange(this.accountEmailTarget)

    this.accountNumberTarget.value = ''
    this.fireOnChange(this.accountNumberTarget)

    this.sortCodeTarget.value = ''
    this.fireOnChange(this.sortCodeTarget)

    this.accountNameTarget.value = ''
    this.fireOnChange(this.accountNameTarget)

    // Hide the account info inputs and show special message
    this.hideAccountInfo()
  }

  fireOnChange(element) {
    // Fire change event manually so that the values get reset
    if ('createEvent' in document) {
      let evt = document.createEvent('HTMLEvents')
      evt.initEvent('change', false, true)
      element.dispatchEvent(evt)
    } else element.fireEvent('onchange')
  }

  bandSpaceValueChanged(value) {
    // Once band space value has changed, update the booking overview
    // Show price and tbc for other values in case no specific band space in booking overview
    if (this.noValue(value)) {
      this.bookingMonthlyPaymentTarget.innerHTML = `£${this.schoolValue.pounds} <span class="text-xs text-gray-700">(once lessons start)</span>`
      this.bookingStartDateTarget.innerText = 'TBC'
      this.bookingFirstPaymentDateTarget.innerText = 'TBC'
      this.bookingFirstPaymentAmountTarget.innerText = 'TBC'
      this.bookingFirstPaymentCoversTarget.innerText = 'TBC'
      this.compulsoryPaymentInfoValue = false
      // don't show payment info if no band space selected
      this.hidePaymentInfoFields()
    } else {
      // Display specific values of the band space in booking overview
      this.bookingStartDateTarget.innerText = value.nextNewcommerClass
      this.bookingMonthlyPaymentTarget.innerText = value.monthlyPayment
      this.bookingFirstPaymentDateTarget.innerText = value.firstPaymentDate
      this.bookingFirstPaymentAmountTarget.innerText = value.firstPayment
      this.bookingFirstPaymentCoversTarget.innerText = value.firstPaymentCovers
      this.compulsoryPaymentInfoValue = true
      this.showPaymentInfoFields()
    }
  }

  compulsoryPaymentInfoValueChanged(value) {
    // Toggle the overarching payment info div depending on if payment info is compulsory (if directly enrolling to a band space)
    if (value === false && this.hasPaymentInfoFieldsTarget) {
      // Below function does nothing
      this.enableBookBtn()
      // this.fillPaymentInfoTarget.classList.remove('hidden'); Remove fill payment info anyway field
    } else if (value === true && this.hasPaymentInfoFieldsTarget) {
      // Below function does nothing
      this.disableBookBtn()
      // this.fillPaymentInfoTarget.classList.add('hidden'); Remove fill payment info anyway field
    }
  }

  streamed(event, context) {
    // When new band spaces are turbo streamed, restore the band space and instrument preference values
    context.bandSpaceValue = {}
    context.instrumentPreferenceValue = {}
  }

  paymentInfoFilled() {
    // Check if all payment infos have a value
    return (
      !this.noValue(this.accountNumberValue) &&
      !this.noValue(this.accountNameValue) &&
      !this.noValue(this.accountEmailValue) &&
      !this.noValue(this.sortCodeValue) &&
      !this.noValue(this.accountHolderFirstNameValue) &&
      !this.noValue(this.accountHolderLastNameValue)
    )
  }

  showPaymentInfoFields() {
    // If user needs to fill out payment info or if they selected the option to fill them out set value to true
    if (this.hasPaymentInfoFieldsTarget) this.showPaymentInfoFieldsValue = true
    this.disclaimerTarget.innerHTML = `
      <p>
        <span class="text-xs">*</span>
        Actual band space information may vary due to the nature of band lessons.
      </p>
    `
    if (this.priceRiseWarningValue) {
      this.disclaimerTarget.innerHTML += `
        <p>
          <span class="text-xs">**</span>
          Monthly subscription price will be £45.99 from 1 September 2026.
        </p>
      `
    }
  }

  hidePaymentInfoFields() {
    // If user does not want to fill out payment fields, set value to false
    this.showPaymentInfoFieldsValue = false
    this.disclaimerTarget.innerHTML = `
      <p>
        <span class="text-base">*</span>
        As soon as a band space becomes available, an email notification will be sent to you. Please make sure to add info@irockschool.com to your list of safe senders.
      </p>
    `
    if (this.priceRiseWarningValue) {
      this.disclaimerTarget.innerHTML += `
        <p>
          <span class="text-xs">**</span>
          Monthly subscription price will be £45.99 from 1 September 2026.
        </p>
      `
    }
  }

  showPaymentInfoFieldsValueChanged(value) {
    // Upon change of value, toggle payment fields and disable/enable book button
    if (value === true && this.hasPaymentInfoFieldsTarget) {
      this.paymentInfoFieldsTarget.classList.remove('hidden')
      // FIX: this function does nothing since 'proceed' is not handled by disabled attribute of book button anymore
      this.disableBookBtn()
    } else if (value === false && this.hasPaymentInfoFieldsTarget) {
      this.paymentInfoFieldsTarget.classList.add('hidden')
      // FIX: this function does nothing since 'proceed' is not handled by disabled attribute of book button anymore
      this.enableBookBtn()
    }
  }

  hideAccountInfo() {
    // Hide payment account fields when click on 'not sole account holder' and show special direct debit message
    this.accountInfoTarget.classList.add('hidden')
    this.specialAccountInfoTarget.classList.remove('hidden')
    // FIX: this function does nothing since 'proceed' is not handled by disabled attribute of book button anymore
    this.enableBookBtn()
  }

  showAccountInfo() {
    // Shows payment account fields when click on sole account holder and hide direct debit message
    this.accountInfoTarget.classList.remove('hidden')
    this.specialAccountInfoTarget.classList.add('hidden')
  }

  disableBookBtn() {
    // FIX: this function does nothing since 'proceed' is not handled by disabled attribute of book button anymore
    // this.submitTarget.setAttribute('disabled', 'disabled');
  }

  enableBookBtn() {
    // FIX: this function does nothing since 'proceed' is not handled by disabled attribute of book button anymore
    // this.submitTarget.removeAttribute('disabled');
  }

  // Fill in the 123formbuilder form with the school name
  fillRegisterInterestForm(schoolName) {
    const div = document.querySelector(`div[data-hash="0000000f"]`)
    const input = div.querySelector('input')
    input.value = schoolName
  }

  // Go to the first slide of the form
  goToFirstSlide() {
    this.indexValue = 0
  }
}
